<script setup>
import GroupedButtons from "./GroupedButtons.vue";
import PasswordField from "./PasswordField.vue";
import PhotoField from "./PhotoField.vue";
import {ref, watch} from "vue";
import {useForm} from "laravel-precognition-vue";
import httpClient from "../lib/http/index.js";

const http = httpClient();

const loading = ref( true );
const error_message = ref( null );
const success_message = ref( null );
const profile_picture = ref( null );
const profile_form = useForm('post', '/api/v1/auth/me', {
    gender: 'man',
    age_ranch: 'adult',
    firstname: '',
    lastname: '',
    email: '',
    phone_number: '',
    password: '',
    password_confirmation: '',
    picture: null,
});
const choicesGenderLabels = {'man': 'un homme', 'woman': 'une femme'};
const choicesAgeRanchLabels = {
    man: {'adult': 'un adulte', 'child': 'un enfant'},
    woman: {'adult': 'une adulte', 'child': 'une enfant'},
};
const choicesAgeRanch = ref( choicesAgeRanchLabels['man'] );

let auth_signal = null;

watch( () => profile_form.gender, value => choicesAgeRanch.value = choicesAgeRanchLabels[ value ] );

async function submit() {
    loading.value = true;

    try {
        const response = await profile_form.submit({
            preserveScroll: true,
            forceFormData: true,
            headers: {
                'Content-Type': 'multipart/form-data'
            },
        });

        if ( response.data.status ) {
            success_message.value = response.data.message;

            await authCheck();

            setTimeout( () => success_message.value = null, 5000 );
        }
    } catch (e) {
        console.error( e.message );
        loading.value = false;
    }
}

async function authCheck() {
    try {
        if ( auth_signal ) {
            auth_signal.abort('Cancel request');
        }

        let response;

        auth_signal = http.signal();

        response = await http.get('/api/v1/auth/me', {}, {
            signal: auth_signal.signal
        });

        const user = response.body;

        profile_form.firstname = user.firstname;
        profile_form.lastname = user.lastname;
        profile_form.email = user.email;
        profile_form.phone_number = user.phone_number;
        profile_form.gender = user.gender;
        profile_form.age_ranch = user.age_ranch;
        profile_picture.value = user.picture;

        auth_signal = null;

        return user;
    } catch (e) {
        console.error( e.message );
        auth_signal = null;
    }
}

(async () => {
    await authCheck();
})();
</script>

<template>
    <div>
        <div class="border-b p-5 sm:p-10">
            <h3 class="text-white text-xl font-bold mb-5">Mes informations personnelles</h3>

            <div class="flex flex-row mb-10">
                <label class="text-white flex flex-col justify-center"><span class="important">Je suis</span></label>
                <div class="flex-1 ml-5 lg:ml-10">
                    <grouped-buttons class="w-full" @input="value => profile_form.gender = value"
                                     :value="profile_form.gender" :choices="choicesGenderLabels"></grouped-buttons>
                </div>
            </div>

            <div class="flex flex-row mb-10">
                <label class="text-white flex flex-col justify-center"><span class="important">Je suis</span></label>
                <div class="flex-1 ml-5 lg:ml-10">
                    <grouped-buttons class="w-full" @input="value => profile_form.age_ranch = value"
                                     :value="profile_form.age_ranch" :choices="choicesAgeRanch"></grouped-buttons>
                </div>
            </div>

            <div class="flex flex-col lg:flex-row gap-y-5 gap-x-10 mb-10">
                <div class="w-full lg:w-1/2">
                    <label class="important | text-white block mb-2" for="input_firstname">Nom</label>
                    <input type="text" id="input_firstname" class="w-full py-1 px-2"
                           v-model="profile_form.firstname">
                    <div class="text-red-500 mt-2.5" v-if="profile_form.invalid('firstname')">
                        {{ profile_form.errors.firstname }}
                    </div>
                </div>

                <div class="w-full lg:w-1/2">
                    <label class="important | text-white block mb-2" for="input_lastname">Prénom</label>
                    <input type="text" id="input_lastname" class="w-full py-1 px-2"
                           v-model="profile_form.lastname">
                    <div class="text-red-500 mt-2.5" v-if="profile_form.invalid('lastname')">
                        {{ profile_form.errors.lastname }}
                    </div>
                </div>
            </div>

            <div class="flex flex-col lg:flex-row gap-y-5 gap-x-10 mb-10">
                <div class="w-full lg:w-1/2">
                    <label class="important | text-white block mb-2" for="input_phone">Numéro de téléphone</label>
                    <input type="text" id="input_phone" class="w-full py-1 px-2"
                           v-model="profile_form.phone_number">
                    <div class="text-red-500 mt-2.5" v-if="profile_form.invalid('phone_number')">
                        {{ profile_form.errors.phone_number }}
                    </div>
                </div>
                <div class="w-full lg:w-1/2 text-white flex flex-col justify-end">
                    En cas de personne mineure, veuillez indiquer le numéro de téléphone du responsable légal.
                </div>
            </div>

            <div class="flex flex-row lg:gap-x-10">
                <div class="w-full lg:w-2/3">
                    <label class="text-white block mb-2" for="input_picture">Photo de profil</label>
                    <photo-field name="picture" :image="profile_picture" @input="value => profile_form.picture = value" v-if="profile_picture">
                        Max 100 ko<br/>Max 500*500px<br/>Format .jpg, .jpeg ou .png
                    </photo-field>
                </div>
                <div class="w-0 lg:w-1/3"></div>
            </div>
        </div>

        <div class="p-10">
            <h3 class="text-white text-xl font-bold mb-5">Mes informations de connexion</h3>

            <div class="flex flex-col lg:flex-row gap-x-10 mb-5 lg:mb-10">
                <div class="w-full lg:w-1/2">
                    <label class="important | text-white block mb-2" for="input_email">Adresse email</label>
                    <input type="email" id="input_email" class="w-full py-1 px-2"
                           v-model="profile_form.email">
                    <div class="text-red-500 mt-2.5" v-if="profile_form.invalid('email')">
                        {{ profile_form.errors.email }}
                    </div>
                </div>
                <div class="w-0 lg:w-1/2"></div>
            </div>

            <div class="flex flex-col lg:flex-row gap-y-5 gap-x-10 mb-10">
                <div class="w-full lg:w-1/2">
                    <label class="important | text-white block mb-2" for="input_password">Mot de passe</label>
                    <password-field name="password" id="input_password" v-model="profile_form.password"></password-field>
                    <div class="text-red-500 mt-2.5" v-if="profile_form.invalid('password')">
                        {{ profile_form.errors.password }}
                    </div>
                </div>

                <div class="w-full lg:w-1/2">
                    <label class="important | text-white block mb-2" for="input_password_confirmation">Confirmer</label>
                    <password-field name="password" id="input_password_confirmation" v-model="profile_form.password_confirmation"></password-field>
                    <div class="text-red-500 mt-2.5" v-if="profile_form.invalid('password_confirmation')">
                        {{ profile_form.errors.password_confirmation }}
                    </div>
                </div>
            </div>

            <div class="flex flex-col gap-y-10">
                <div class="w-full lg:w-1/2 mx-auto px-5">
                    <button type="button" :disabled="profile_form.processing" @click="submit"
                            class="button | w-full py-2 disabled:opacity-50 disabled:cursor-not-allowed">
                        <span v-if="!profile_form.processing">Mettre à jour</span>
                        <i v-if="profile_form.processing" class="fa-solid fa-spinner fa-spin"></i>
                    </button>
                </div>

                <transition>
                    <div class="w-full lg:w-1/2 mx-auto text-red-500 text-center" v-if="error_message" v-html="error_message"></div>
                </transition>

                <transition>
                    <div class="w-full lg:w-1/2 mx-auto text-green-500 text-center" v-if="success_message" v-html="success_message"></div>
                </transition>
            </div>
        </div>
    </div>
</template>